import * as THREE from 'three';
import { useGalaxyStore } from '../core/state/GalaxyStore';
import { MouseController } from './controllers/MouseController';
import { KeyboardController } from './controllers/KeyboardController';
import { TouchController } from './controllers/TouchController';
import { SelectionManager } from './managers/SelectionManager';
import { FeedbackManager } from './managers/FeedbackManager';
import { CameraController } from '../visualization/camera/CameraController';
import { SceneHierarchyValidator, validateSceneHierarchy } from '../visualization/utils/SceneHierarchyValidator';

export interface InteractionConfig {
  enableMouse: boolean;
  enableKeyboard: boolean;
  enableTouch: boolean;
  raycastThrottle: number;
  maxInteractiveObjects: number;
}

const DEFAULT_CONFIG: InteractionConfig = {
  enableMouse: true,
  enableKeyboard: true,
  enableTouch: true,
  raycastThrottle: 33, // ~30fps
  maxInteractiveObjects: 1000,
};

export class InteractionManager {
  private config: InteractionConfig;
  private canvas: HTMLCanvasElement;
  private camera: THREE.Camera;
  private scene: THREE.Scene;
  private store: typeof useGalaxyStore;
  private cameraController: CameraController;
  
  // Input controllers
  private mouseController: MouseController;
  private keyboardController: KeyboardController;
  private touchController: TouchController;
  
  // Managers
  private selectionManager: SelectionManager;
  private feedbackManager: FeedbackManager;
  private hierarchyValidator: SceneHierarchyValidator;
  
  // State
  private isEnabled: boolean = true;
  private currentInputMode: 'mouse' | 'touch' | 'keyboard' = 'mouse';
  
  constructor(
    canvas: HTMLCanvasElement,
    camera: THREE.Camera,
    scene: THREE.Scene,
    store: typeof useGalaxyStore,
    cameraController: CameraController,
    config?: Partial<InteractionConfig>
  ) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.canvas = canvas;
    this.camera = camera;
    this.scene = scene;
    this.store = store;
    this.cameraController = cameraController;
    
    // Initialize managers
    this.selectionManager = new SelectionManager(
      camera,
      scene,
      this.config.raycastThrottle
    );
    
    this.feedbackManager = new FeedbackManager(canvas);
    
    this.hierarchyValidator = new SceneHierarchyValidator(scene, this.selectionManager);
    
    // Initialize input controllers
    this.mouseController = new MouseController(
      canvas,
      this.selectionManager,
      this.feedbackManager,
      store
    );
    
    this.keyboardController = new KeyboardController(
      cameraController,
      store,
      scene  // Pass scene reference for object lookup
    );
    
    this.touchController = new TouchController(
      canvas,
      this.selectionManager,
      this.feedbackManager,
      store,
      cameraController
    );
    
    this.initialize();
  }
  
  private initialize(): void {
    // Set up input detection
    this.detectInputCapabilities();
    
    // Enable controllers based on config
    if (this.config.enableMouse) {
      this.mouseController.enable();
    }
    
    if (this.config.enableKeyboard) {
      this.keyboardController.enable();
    }
    
    if (this.config.enableTouch) {
      this.touchController.enable();
    }
    
    console.log('InteractionManager initialized with capabilities:', {
      mouse: this.config.enableMouse,
      keyboard: this.config.enableKeyboard,
      touch: this.config.enableTouch,
    });
    
    // Reset any corrupted mining collector materials from previous hover effects
    this.selectionManager.resetMiningCollectorMaterials();
    
    // Validate scene hierarchy for interactive objects
    this.validateSceneHierarchy();
  }
  
  private detectInputCapabilities(): void {
    // Detect touch capability
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Detect mouse
    const hasMouse = window.matchMedia('(pointer: fine)').matches;
    
    // Update current input mode based on capabilities
    if (hasTouch && !hasMouse) {
      this.currentInputMode = 'touch';
    } else {
      this.currentInputMode = 'mouse';
    }
    
    console.log('Input capabilities detected:', {
      hasTouch,
      hasMouse,
      currentMode: this.currentInputMode,
    });
  }
  
  public update(deltaTime: number): void {
    if (!this.isEnabled) return;
    
    this.selectionManager.update(deltaTime);
    this.feedbackManager.update(deltaTime);
    this.keyboardController.update(deltaTime);
  }
  
  public enable(): void {
    this.isEnabled = true;
    
    if (this.config.enableMouse) {
      this.mouseController.enable();
    }
    
    if (this.config.enableKeyboard) {
      this.keyboardController.enable();
    }
    
    if (this.config.enableTouch) {
      this.touchController.enable();
    }
  }
  
  public disable(): void {
    this.isEnabled = false;
    this.mouseController.disable();
    this.keyboardController.disable();
    this.touchController.disable();
  }
  
  public getHoveredObject(): THREE.Object3D | null {
    return this.selectionManager.getHoveredObject();
  }
  
  public getSelectedObject(): THREE.Object3D | null {
    const selectedId = this.store.getState().ui.selectedObject;
    if (!selectedId) return null;
    
    // Find object in scene by ID
    let foundObject: THREE.Object3D | null = null;
    this.scene.traverse((object) => {
      if (object.userData['id'] === selectedId) {
        foundObject = object;
      }
    });
    
    return foundObject;
  }
  
  /**
   * Validate scene hierarchy for interactive objects
   */
  private validateSceneHierarchy(): void {
    setTimeout(() => {
      const result = this.hierarchyValidator.validateInteractiveObjects();
      
      if (!result.isValid) {
        console.warn('🚨 Scene Hierarchy Validation Failed:');
        console.warn('Issues:', result.hierarchyIssues);
        console.warn('Recommendations:', result.recommendations);
        console.log('Full report:', this.hierarchyValidator.getDebugReport());
      } else {
        console.log('✅ Scene hierarchy validation passed:', {
          totalObjects: result.totalObjects,
          interactiveObjects: result.interactiveObjects.length
        });
      }
    }, 100); // Small delay to ensure objects are fully initialized
  }

  /**
   * Revalidate scene hierarchy - call when new interactive objects are added
   */
  public revalidateSceneHierarchy(): void {
    this.validateSceneHierarchy();
  }

  /**
   * Test if a specific object is detectable by the interaction system
   */
  public testObjectInteractivity(objectId: string): boolean {
    const validation = this.hierarchyValidator.testObjectInteractivity(objectId);
    
    if (!validation) {
      console.warn(`Object ${objectId} not found in scene`);
      return false;
    }

    if (!validation.isDetectable) {
      console.warn(`Object ${objectId} is not detectable:`, validation.issues);
      return false;
    }

    console.log(`Object ${objectId} is detectable ✅`);
    return true;
  }

  /**
   * Reset mining collector materials - useful for fixing corrupted hover effects
   */
  public resetMiningCollectorMaterials(): void {
    this.selectionManager.resetMiningCollectorMaterials();
  }

  public dispose(): void {
    this.disable();
    this.mouseController.dispose();
    this.keyboardController.dispose();
    this.touchController.dispose();
    this.selectionManager.dispose();
    this.feedbackManager.dispose();
  }
}